function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

// Optimize dispatchCustomEvent with single object creation
const dispatchCustomEvent = (eventName, data = {}) => {
  document.dispatchEvent(new CustomEvent(eventName, data ? { detail: data } : null));
};

window.recentlyViewedIds = [];

if (!customElements.get('quantity-selector')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.input = this.querySelector('.qty');
      this.step = parseFloat(this.input.getAttribute('step')) || 1;
      this.changeEvent = new Event('change', { bubbles: true });

      // Cache button references
      this.subtract = this.querySelector('.minus');
      this.add = this.querySelector('.plus');

      // Use arrow functions to avoid bind
      this.subtract.addEventListener('click', () => this.changeQuantity(-this.step));
      this.add.addEventListener('click', () => this.changeQuantity(this.step));

      this.classList.add('buttons_added');
      this.validateQtyRules();
    }

    changeQuantity(change) {
      const quantity = parseInt(this.input.value) || 0;
      const newValue = Math.max(quantity + change, 1);
      const min = parseInt(this.input.min) || 1;
      const max = parseInt(this.input.max) || Infinity;
      
      if (newValue < min || newValue > max) return;

      this.input.value = newValue;
      this.input.dispatchEvent(this.changeEvent);
      this.validateQtyRules();
    }

    validateQtyRules() {
      const value = parseInt(this.input.value) || 0;
      const min = parseInt(this.input.min) || 1;
      const max = parseInt(this.input.max) || Infinity;

      this.subtract.classList.toggle('disabled', value <= min);
      this.add.classList.toggle('disabled', value >= max);
    }
  }

  customElements.define('quantity-selector', QuantityInput);
}

/**
 *  @class
 *  @function ProductCard
 */
if (!customElements.get('product-card')) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();
      // Cache all selectors in constructor
      this.swatches = this.querySelector('.product-card-swatches');
      this.image = this.querySelector('.product-card-image-link .product-primary-image');
      this.additional_images = this.querySelectorAll('.product-secondary-image');
      this.additional_images_nav = this.querySelectorAll('.product-secondary-images-nav li');
      this.quick_add = this.querySelector('.product-card--add-to-cart-button-simple');
    }

    connectedCallback() {
      // Use single RAF for all initialization
      requestAnimationFrame(() => {
        if (this.swatches) this.enableSwatches();
        if (this.additional_images.length) this.enableAdditionalImages();
        if (this.quick_add) this.enableQuickAdd();
      });
    }

    enableAdditionalImages() {
      const image_length = this.additional_images.length;
      if (!image_length) return;

      const images = Array.from(this.additional_images);
      const nav = Array.from(this.additional_images_nav);
      const image_container = this.querySelector('.product-card-image');
      
      if (!image_container) return;

      // Pre-calculate container width to avoid repeated getBoundingClientRect calls
      let containerWidth = image_container.offsetWidth;
      let activeIndex = -1;

      // Optimized mousemove handler with RAF throttling
      let rafId = null;
      const updateHover = (offsetX) => {
        const prc = offsetX / containerWidth;
        const newIndex = Math.min(Math.floor(prc * image_length), image_length - 1);
        
        if (newIndex === activeIndex || newIndex < 0) return;
        
        // Remove previous hover state
        if (activeIndex >= 0) {
          images[activeIndex].classList.remove('hover');
          if (nav[activeIndex]) nav[activeIndex].classList.remove('active');
        }
        
        // Add new hover state
        activeIndex = newIndex;
        images[activeIndex].classList.add('hover');
        if (nav[activeIndex]) nav[activeIndex].classList.add('active');
      };

      const mousemove = (e) => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          updateHover(e.offsetX);
          rafId = null;
        });
      };

      const mouseleave = () => {
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        if (activeIndex >= 0) {
          images[activeIndex].classList.remove('hover');
          if (nav[activeIndex]) nav[activeIndex].classList.remove('active');
          activeIndex = -1;
        }
      };

      // Update container width on resize
      const resizeObserver = new ResizeObserver(() => {
        containerWidth = image_container.offsetWidth;
      });
      resizeObserver.observe(image_container);

      // Use passive event listeners for better scroll performance
      const eventOptions = { passive: true };
      image_container.addEventListener('touchstart', mousemove, eventOptions);
      image_container.addEventListener('touchmove', mousemove, eventOptions);
      image_container.addEventListener('touchend', mouseleave, eventOptions);
      image_container.addEventListener('mouseenter', mousemove, eventOptions);
      image_container.addEventListener('mousemove', mousemove, eventOptions);
      image_container.addEventListener('mouseleave', mouseleave, eventOptions);

      // Lazy load images only once on window load
      window.addEventListener('load', () => {
        if (typeof lazySizes !== 'undefined' && lazySizes.loader) {
          images.forEach(image => lazySizes.loader.unveil(image));
        }
      }, { once: true });
    }

    enableSwatches() {
      const swatch_list = this.swatches.querySelectorAll('.product-card-swatch');
      this.color_index = this.swatches.dataset.index;
      this.org_srcset = this.image?.dataset.srcset || '';

      // Use event delegation for better performance
      this.addEventListener('change', this.onSwatchChange.bind(this));

      // Preload swatch images on idle
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          swatch_list.forEach(swatch => {
            if (swatch.dataset.srcset) {
              const link = document.createElement('link');
              link.rel = 'preload';
              link.as = 'image';
              link.href = swatch.dataset.srcset.split(' ')[0];
              document.head.appendChild(link);
            }
          });
        });
      }

      swatch_list.forEach(swatch => {
        swatch.addEventListener('click', this.onSwatchClick);
        swatch.addEventListener('mouseover', this.onSwatchHover);
      });
    }

    onSwatchClick(e) {
      e.preventDefault();
      window.location.href = this.dataset.href;
    }

    onSwatchHover(e) {
      const swatch_input = this.previousElementSibling;
      swatch_input.checked = true;
      swatch_input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onSwatchChange(evt) {
      if (this.image && evt.target.dataset.srcset) {
        this.image.setAttribute('srcset', evt.target.dataset.srcset || this.org_srcset);
      }
    }

    enableQuickAdd() {
      this.quick_add.addEventListener('click', this.quickAdd.bind(this));
    }

    async quickAdd(evt) {
      evt.preventDefault();
      if (this.quick_add.disabled) return;

      this.quick_add.classList.add('loading');
      this.quick_add.disabled = true;

      const formData = new FormData();
      formData.append('id', this.quick_add.dataset.productId);
      formData.append('quantity', 1);
      
      const sections = this.getSectionsToRender();
      formData.append('sections', sections.map(s => s.section));
      formData.append('sections_url', window.location.pathname);

      try {
        const response = await fetch(`${theme.routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/javascript'
          },
          body: formData
        });

        const data = await response.json();
        if (!data.status) {
          this.renderContents(data);
          dispatchCustomEvent('cart:item-added', {
            product: data.items?.[0] || data
          });
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.quick_add.classList.remove('loading');
        this.quick_add.disabled = false;
      }
    }

    getSectionsToRender() {
      return [
        { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-secondary-area--item-count' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-secondary-area--item-amount' }
      ];
    }

    renderContents(parsedState) {
      const parser = new DOMParser();
      
      this.getSectionsToRender().forEach(section => {
        const element = document.getElementById(section.id);
        if (!element) return;
        
        const target = element.querySelector(section.selector) || element;
        const html = parsedState.sections[section.section];
        if (html) {
          const doc = parser.parseFromString(html, 'text/html');
          const content = doc.querySelector(section.selector)?.innerHTML;
          if (content) target.innerHTML = content;
        }
      });

      const drawer = document.getElementById('Cart-Drawer');
      if (drawer) {
        drawer.open();
        drawer.removeProductEvent();
      }
    }
  }
  
  customElements.define('product-card', ProductCard);
}

/**
 *  @class
 *  @function PanelClose
 */
if (!customElements.get('side-panel-close')) {
  class PanelClose extends HTMLElement {
    constructor() {
      super();
      this.clickCapture = document.querySelector('.click-capture');
      
      // Bind methods once
      this.closePanelHandler = this.close_panel.bind(this);
      this.closeActivePanel = (e) => {
        const panel = document.querySelector('.side-panel.active');
        if (panel) this.close_panel(e, panel);
      };
    }

    connectedCallback() {
      this.addEventListener('click', this.closePanelHandler);
      document.addEventListener('panel:close', this.closeActivePanel);
      if (this.clickCapture) {
        this.clickCapture.addEventListener('click', this.closeActivePanel);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('panel:close', this.closeActivePanel);
      if (this.clickCapture) {
        this.clickCapture.removeEventListener('click', this.closeActivePanel);
      }
    }

    close_panel(e, panel) {
      if (e) e.preventDefault();
      
      if (!panel) {
        panel = e?.target.closest('.side-panel');
        if (!panel) return;
      }
      
      panel.classList.remove('active');
      document.body.classList.remove('open-cc');
      panel.setAttribute('inert', '');
    }
  }
  
  customElements.define('side-panel-close', PanelClose);

  // Single global escape key handler
  document.addEventListener('keyup', (e) => {
    if (e.code?.toUpperCase() === 'ESCAPE') {
      dispatchCustomEvent('panel:close');
    }
  });
}

/**
 *  @class
 *  @function CartDrawer
 */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
    }

    connectedCallback() {
      this.button = document.getElementById('cart-drawer-toggle');
      
      // Bind methods once
      this.debouncedOnChange = debounce(this.onChange.bind(this), 300);
      this.windowClickHandler = this.onWindowClick.bind(this);
      this.refreshHandler = () => this.refresh();
      this.toggleHandler = (e) => {
        e.preventDefault();
        this.isOpen ? this.close() : this.open();
      };
      this.escapeHandler = (e) => {
        if (e.code?.toUpperCase() === 'ESCAPE' && this.isOpen) {
          this.close();
        }
      };

      // Add event listeners
      document.addEventListener('cart:refresh', this.refreshHandler);
      this.addEventListener('change', this.debouncedOnChange);
      document.addEventListener('click', this.windowClickHandler);
      
      if (this.button) {
        this.button.addEventListener('click', this.toggleHandler);
      }
      
      document.addEventListener('keyup', this.escapeHandler);
      
      this.removeProductEvent();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:refresh', this.refreshHandler);
      document.removeEventListener('click', this.windowClickHandler);
      document.removeEventListener('keyup', this.escapeHandler);
      if (this.button) {
        this.button.removeEventListener('click', this.toggleHandler);
      }
    }

    onWindowClick(event) {
      if (!this.contains(event.target) && this.isOpen && !this.button?.contains(event.target)) {
        this.close();
      }
    }

    open() {
      this.removeAttribute('inert');
      this.focus();
      this.isOpen = true;
      dispatchCustomEvent('cart-drawer:open');
    }

    close() {
      this.setAttribute('inert', '');
      this.isOpen = false;
    }

    onChange(event) {
      if (event.target.classList.contains('qty')) {
        this.updateQuantity(event.target.dataset.index, event.target.value);
      }
    }

    removeProductEvent() {
      // Use event delegation instead of multiple listeners
      if (!this.removeClickHandler) {
        this.removeClickHandler = (event) => {
          if (event.target.classList.contains('remove')) {
            event.preventDefault();
            this.updateQuantity(event.target.dataset.index, '0');
          }
        };
        this.addEventListener('click', this.removeClickHandler);
      }
    }

    getSectionsToRender() {
      return [
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer--inner' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-secondary-area--item-count' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-secondary-area--item-amount' }
      ];
    }

    async updateQuantity(line, quantity) {
      const lineItem = this.querySelector(`#CartDrawerItem-${line}`);
      lineItem?.classList.add('thb-loading');

      const sections = this.getSectionsToRender();
      const body = JSON.stringify({
        line,
        quantity,
        sections: sections.map(s => s.section),
        sections_url: window.location.pathname
      });

      dispatchCustomEvent('line-item:change:start', { quantity });

      try {
        const response = await fetch(`${theme.routes.cart_change_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body
        });

        const parsedState = await response.json();
        
        if (parsedState.sections) {
          const parser = new DOMParser();
          
          sections.forEach(section => {
            const element = document.getElementById(section.id);
            if (!element) return;
            
            const target = element.querySelector(section.selector) || element;
            const html = parsedState.sections[section.section];
            
            if (html) {
              const doc = parser.parseFromString(html, 'text/html');
              const content = doc.querySelector(section.selector)?.innerHTML;
              if (content) target.innerHTML = content;
            }
          });
        }

        dispatchCustomEvent('line-item:change:end', { quantity, cart: parsedState });
      } catch (error) {
        console.error('Cart update error:', error);
      } finally {
        lineItem?.classList.remove('thb-loading');
      }
    }

    async refresh() {
      const sections = this.getSectionsToRender();
      const sectionNames = sections.map(s => s.section).join(',');
      
      try {
        const response = await fetch(`${window.location.pathname}?sections=${sectionNames}`);
        const parsedState = await response.json();
        const parser = new DOMParser();

        sections.forEach(section => {
          const element = document.getElementById(section.id);
          if (!element) return;
          
          const target = element.querySelector(section.selector) || element;
          const html = parsedState[section.section];
          
          if (html) {
            const doc = parser.parseFromString(html, 'text/html');
            const content = doc.querySelector(section.selector)?.innerHTML;
            if (content) target.innerHTML = content;
          }
        });
      } catch (error) {
        console.error('Cart refresh error:', error);
      }
    }
  }
  
  customElements.define('cart-drawer', CartDrawer);
}

/**
 *  @class
 *  @function LocalizationForm
 */
if (!customElements.get('localization-form')) {
  class LocalizationForm extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      
      this.debouncedSubmit = debounce(() => this.form.submit(), 200);
      
      // Use event delegation for better performance
      this.form.addEventListener('change', (e) => {
        if (e.target.matches('[name="locale_code"], [name="country_code"]')) {
          this.debouncedSubmit();
        }
      });
    }
  }
  
  customElements.define('localization-form', LocalizationForm);
}

/**
 *  @class
 *  @function ResizeSelect
 */
if (!customElements.get('resize-select')) {
  class ResizeSelect extends HTMLElement {
    constructor() {
      super();
      this.resizeHandler = this.resizeSelect.bind(this);
    }

    connectedCallback() {
      this.select = this.querySelector('select');
      if (!this.select) return;
      
      this.addEventListener('change', this.resizeHandler);
      
      // Use RAF for initial resize
      requestAnimationFrame(() => this.resizeSelect());
    }

    resizeSelect() {
      if (!this.select?.selectedOptions[0]) return;
      
      const text = this.select.selectedOptions[0].textContent;
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      // Use canvas to measure text width (faster than DOM manipulation)
      context.font = getComputedStyle(this.select).font;
      const width = context.measureText(text).width;
      
      this.select.style.width = `${Math.ceil(width) + 20}px`;
    }
  }
  
  customElements.define('resize-select', ResizeSelect);
}

/**
 *  @class
 *  @function QuickView
 */
if (!customElements.get('quick-view')) {
  class QuickView extends HTMLElement {
    constructor() {
      super();
      this.loading = false;
    }

    connectedCallback() {
      this.drawer = document.getElementById('Product-Modal');
      
      // Single event handler for both click and keypress
      this.addEventListener('click', this.handleInteraction.bind(this));
      this.addEventListener('keypress', this.handleInteraction.bind(this));
    }

    handleInteraction(e) {
      if (e.type === 'keypress' && e.key !== 'Enter') return;
      if (this.loading) return;
      
      e.preventDefault();
      this.loadQuickView();
    }

    async loadQuickView() {
      const productHandle = this.dataset.productHandle;
      if (!productHandle) return;
      
      let href = `${theme.routes.root_url}/products/${productHandle}?view=quick-view`;
      href = href.replace('//', '/');
      
      this.loading = true;
      this.classList.add('loading');
      
      try {
        const response = await fetch(href);
        const text = await response.text();
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const content = doc.querySelector('#Product-Modal-Content')?.innerHTML;
        
        if (content) {
          this.renderQuickview(content, href, productHandle);
        }
      } catch (error) {
        console.error('Quick view error:', error);
      } finally {
        this.loading = false;
        this.classList.remove('loading');
      }
    }

    renderQuickview(sectionInnerHTML, href, productHandle) {
      const modalContent = this.drawer.querySelector('#Product-Modal-Content');
      if (!modalContent) return;
      
      modalContent.innerHTML = sectionInnerHTML;

      // Load scripts efficiently
      const scripts = modalContent.querySelectorAll('script[src]');
      if (scripts.length) {
        const fragment = document.createDocumentFragment();
        scripts.forEach(script => {
          const newScript = document.createElement('script');
          newScript.src = script.src;
          newScript.async = true;
          fragment.appendChild(newScript);
        });
        document.head.appendChild(fragment);
      }

      // Defer non-critical tasks
      requestIdleCallback(() => {
        if (window.Shopify?.PaymentButton) {
          Shopify.PaymentButton.init();
        }
        if (window.ProductModel) {
          ProductModel.loadShopifyXR();
        }
      });

      this.drawer.setAttribute('open', '');
      this.drawer.querySelector('.product-modal__toggle')?.focus();

      // Reinit slider after render
      requestAnimationFrame(() => {
        const slider = this.drawer.querySelector('#Product-Slider');
        slider?.reInit?.();
        window.dispatchEvent(new Event('resize'));
      });

      dispatchCustomEvent('quick-view:open', { productUrl: href, productHandle });
      addIdToRecentlyViewed(productHandle);
    }
  }
  
  customElements.define('quick-view', QuickView);
}

/**
 *  @class
 *  @function ThemeScroll
 */
if (!customElements.get('theme-scroll')) {
  class ThemeScroll extends HTMLElement {
    constructor() {
      super();
      this.viewport = this.querySelector('.theme-scroll--inner');
      this.slideWidth = 274;
      this.slidesToScroll = 1;
    }

    connectedCallback() {
      if (window.OverlayScrollbarsGlobal) {
        OverlayScrollbarsGlobal.OverlayScrollbars({
          target: this,
          elements: { viewport: this.viewport }
        }, {});
      }
      
      this.init();
    }

    init() {
      this.setSlides();
      if (this.slides.length < 1) return;
      
      this.nav = this.querySelector('.theme-scroll-nav');
      this.navPrev = this.querySelector('.flickity-prev');
      this.navNext = this.querySelector('.flickity-next');
      
      // Use IntersectionObserver for better performance
      this.setupIntersectionObserver();
      
      this.debouncedOnScroll = debounce(() => this.onScroll(), 100);
      this.setListeners();
      
      requestAnimationFrame(() => this.centerArrows());
    }

    setSlides() {
      this.slides = Array.from(this.querySelectorAll('.carousel__slide'));
      this.slidesLength = this.slides.length;
      this.lastSlide = this.slides[this.slidesLength - 1];
    }

    setupIntersectionObserver() {
      const options = {
        root: this.viewport,
        rootMargin: '0px',
        threshold: 0.1
      };
      
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          } else {
            entry.target.classList.remove('is-visible');
          }
        });
        this.onNavDisabled();
      }, options);
      
      this.slides.forEach(slide => this.observer.observe(slide));
    }

    setListeners() {
      // Event delegation for nav clicks
      this.nav?.addEventListener('click', this.onNavClick.bind(this));
      this.viewport.addEventListener('scroll', this.debouncedOnScroll, { passive: true });
      
      // Use ResizeObserver for slide width changes
      if (this.slides[0]) {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.slides[1]) {
            this.slideWidth = this.slides[1].offsetLeft - this.slides[0].offsetLeft;
          }
          this.onScroll();
        });
        this.resizeObserver.observe(this.slides[0]);
      }
    }

    onScroll() {
      const scrollLeft = this.viewport.scrollLeft;
      const maxScroll = this.viewport.scrollWidth - this.viewport.offsetWidth;
      this.reachedEnd = Math.abs(scrollLeft - maxScroll) < 1;
      this.onNavDisabled();
    }

    onNavClick(e) {
      if (!e.target.matches('.flickity-nav')) return;
      
      const direction = e.target.name === 'next' ? 1 : -1;
      const scrollPos = this.viewport.scrollLeft + (direction * this.slidesToScroll * this.slideWidth);
      
      this.viewport.scrollTo({
        left: scrollPos,
        behavior: 'smooth'
      });
    }

    onNavDisabled() {
      if (this.navPrev) {
        this.navPrev.disabled = this.viewport.scrollLeft < 1;
      }
      if (this.navNext) {
        this.navNext.disabled = this.reachedEnd;
      }
    }

    centerArrows() {
      const firstCell = this.slides[0];
      if (!firstCell) return;
      
      let imageHeight = 0;
      const cardImage = firstCell.querySelector('.product-card-image');
      
      if (cardImage) {
        imageHeight = cardImage.clientHeight;
      } else if (firstCell.querySelector('.gallery--item') && this.slides[1]) {
        const secondImage = this.slides[1].querySelector('.product-card-image');
        imageHeight = secondImage ? secondImage.clientHeight : 0;
      }
      
      if (imageHeight > 0) {
        const maxHeight = Math.max(...this.slides.map(slide => slide.clientHeight));
        
        if (maxHeight > imageHeight) {
          const offset = (maxHeight - imageHeight) / -2;
          const transform = `translateY(${offset}px)`;
          
          if (this.navPrev) this.navPrev.style.transform = transform;
          if (this.navNext) this.navNext.style.transform = transform;
        }
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
    }
  }
  
  customElements.define('theme-scroll', ThemeScroll);
}

/**
 *  @class
 *  @function CollapsibleRow
 */
if (!customElements.get('collapsible-row')) {
  class CollapsibleRow extends HTMLElement {
    constructor() {
      super();
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;
    }

    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.content = this.querySelector('.collapsible__content');
      this.collapsed_mobile = this.classList.contains('accordion--collapsed-mobile');
      
      if (!this.initialized) {
        this.summary?.addEventListener('click', this.onClick.bind(this));
        this.initialized = true;
        
        if (this.collapsed_mobile) {
          // Use matchMedia for better performance
          const mediaQuery = window.matchMedia('(min-width: 769px)');
          this.handleMediaChange(mediaQuery);
          mediaQuery.addListener(this.handleMediaChange.bind(this));
        }
      }
    }

    handleMediaChange(mq) {
      if (this.details) {
        this.details.open = mq.matches;
      }
    }

    onClick(e) {
      e.preventDefault();
      
      if (!this.details) return;
      
      this.details.style.overflow = 'hidden';
      
      if (this.isClosing || !this.details.open) {
        this.open();
      } else if (this.isExpanding || this.details.open) {
        this.shrink();
      }
    }

    shrink() {
      this.isClosing = true;
      
      const startHeight = `${this.details.offsetHeight}px`;
      const endHeight = `${this.summary.offsetHeight}px`;
      
      if (this.animation) this.animation.cancel();
      
      this.animation = this.details.animate(
        { height: [startHeight, endHeight] },
        { duration: 250, easing: 'ease' }
      );
      
      this.animation.onfinish = () => this.onAnimationFinish(false);
      this.animation.oncancel = () => this.isClosing = false;
    }

    open() {
      this.details.style.height = `${this.details.offsetHeight}px`;
      this.details.open = true;
      requestAnimationFrame(() => this.expand());
    }

    expand() {
      this.isExpanding = true;
      
      const startHeight = `${this.details.offsetHeight}px`;
      const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;
      
      if (this.animation) this.animation.cancel();
      
      this.animation = this.details.animate(
        { height: [startHeight, endHeight] },
        { duration: 250, easing: 'ease-out' }
      );
      
      this.animation.onfinish = () => this.onAnimationFinish(true);
      this.animation.oncancel = () => this.isExpanding = false;
    }

    onAnimationFinish(open) {
      this.details.open = open;
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;
      this.details.style.height = '';
      this.details.style.overflow = '';
      
      this.dispatchEvent(new Event('animationend', { bubbles: false }));
    }
  }
  
  customElements.define('collapsible-row', CollapsibleRow);
}

/**
 *  @function addIdToRecentlyViewed
 */
function addIdToRecentlyViewed(handle) {
  if (!handle) {
    const product = document.querySelector('.thb-product-detail');
    handle = product?.dataset.handle;
  }
  
  if (!handle || !window.localStorage) return;
  
  try {
    const stored = localStorage.getItem('recently-viewed');
    if (stored) {
      window.recentlyViewedIds = JSON.parse(stored);
    }
    
    // Remove if exists and add to front
    const index = window.recentlyViewedIds.indexOf(handle);
    if (index > -1) {
      window.recentlyViewedIds.splice(index, 1);
    }
    
    window.recentlyViewedIds.unshift(handle);
    
    // Keep only last 10 items
    window.recentlyViewedIds = window.recentlyViewedIds.slice(0, 10);
    
    localStorage.setItem('recently-viewed', JSON.stringify(window.recentlyViewedIds));
  } catch (e) {
    console.error('Recently viewed error:', e);
  }
}

// Initialize scrollbar width once
document.addEventListener('DOMContentLoaded', () => {
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
}, { once: true });